import type { SourceFileType, TargetMimeType } from '../types';

export const MIME_TO_EXT: Record<TargetMimeType, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

export const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif', 'image/tiff'];
export const ACCEPTED_EXT = ['.heic', '.heif', '.tiff', '.tif', '.avif', '.jpg', '.jpeg', '.png', '.webp', '.gif'];

export const getFileExtension = (filename: string): string => {
  const ext = filename.split('.').pop();
  return ext ? ext.toLowerCase() : '';
};

export const getFileType = (file: File): SourceFileType => {
  const ext = getFileExtension(file.name);
  const mime = file.type.toLowerCase();
  if (mime.includes('heic') || mime.includes('heif') || ['heic', 'heif'].includes(ext)) return 'heic';
  if (mime.includes('tiff') || ['tif', 'tiff'].includes(ext)) return 'tiff';
  return 'standard';
};

export const getExtensionFromMime = (mimeType: TargetMimeType): string => MIME_TO_EXT[mimeType] ?? 'jpg';

export const getOutputName = (filename: string, targetFormat: TargetMimeType): string => {
  const dotIndex = filename.lastIndexOf('.');
  const baseName = dotIndex > 0 ? filename.slice(0, dotIndex) : filename;
  return `${baseName}.${getExtensionFromMime(targetFormat)}`;
};

export const isSupportedImageFile = (file: File): boolean => {
  const ext = `.${getFileExtension(file.name)}`;
  return ACCEPTED_MIME.includes(file.type.toLowerCase()) || ACCEPTED_EXT.includes(ext);
};

export const formatBytes = (bytes: number): string =>
  bytes > 1_048_576
    ? `${(bytes / 1_048_576).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * Draw a source Blob onto a canvas, optionally resize, then export.
 */
const canvasExport = (sourceBlob: Blob, targetFormat: TargetMimeType, quality: number, scale = 1): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(sourceBlob);
    img.onload = () => {
      const w = Math.max(1, Math.round(img.naturalWidth * scale));
      const h = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Browser canvas context is unavailable.'));
        return;
      }
      if (targetFormat === 'image/jpeg') {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, w, h);
      }
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(objectUrl);
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error('Canvas toBlob() returned null.')),
        targetFormat,
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Browser could not decode the image source.'));
    };
    img.src = objectUrl;
  });

export const convertImage = async (
  file: File,
  targetFormat: TargetMimeType,
  quality = 0.9,
  scale = 1
): Promise<Blob> => {
  const type = getFileType(file);
  let sourceBlob: Blob = file;
  try {
    if (type === 'heic') {
      const { decodeHeic } = await import('./heicDecoder');
      sourceBlob = await decodeHeic(file);
    } else if (type === 'tiff') {
      const { decodeTiff } = await import('./tiffDecoder');
      sourceBlob = await decodeTiff(file);
    }
    return await canvasExport(sourceBlob, targetFormat, quality, scale);
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown conversion error.';
    throw new Error(`[${file.name}] ${message}`, { cause: err });
  }
};
